import { useEffect, type RefObject } from 'react';

/**
 * Permite arrastrar con el ratón (clic + mover) un contenedor de scroll
 * horizontal, como los carruseles de tarjetas de tours de la home.
 *
 * - El touch sigue usando el scroll nativo (no se interfiere con él).
 * - Mientras se arrastra desactiva el scroll-snap y lo restaura al soltar,
 *   de modo que el arrastre es fluido y al soltar encaja en una tarjeta.
 * - Si el puntero se movió, cancela el clic posterior para que arrastrar no
 *   abra por accidente el enlace de la tarjeta.
 *
 * @param ref      Contenedor con `overflow-x` scrollable.
 * @param enabled  Debe reflejar si el contenedor está montado en el DOM.
 *                 Los carruseles se renderizan de forma diferida (tras
 *                 cargar los tours); cuando `enabled` pasa a `true` el
 *                 efecto se re-ejecuta y engancha los listeners.
 */
export function useDragScroll(
  ref: RefObject<HTMLDivElement | null>,
  enabled: boolean = true,
): void {
  useEffect(() => {
    const el = ref.current;
    if (!enabled || !el) return;

    let isDown = false;
    let startX = 0;
    let startScrollLeft = 0;
    let moved = false;
    const initialSnap = el.style.scrollSnapType || 'x mandatory';

    const onPointerDown = (e: PointerEvent) => {
      // El touch usa el scroll nativo; solo arrastramos con el ratón.
      if (e.pointerType === 'touch' || e.button !== 0) return;
      isDown = true;
      moved = false;
      startX = e.clientX;
      startScrollLeft = el.scrollLeft;
      el.style.cursor = 'grabbing';
      el.style.userSelect = 'none';
      el.style.scrollSnapType = 'none';
    };

    const onPointerMove = (e: PointerEvent) => {
      if (!isDown) return;
      const dx = e.clientX - startX;
      if (Math.abs(dx) > 4) moved = true;
      el.scrollLeft = startScrollLeft - dx;
    };

    const endDrag = () => {
      if (!isDown) return;
      isDown = false;
      el.style.cursor = 'grab';
      el.style.removeProperty('user-select');
      el.style.scrollSnapType = initialSnap; // vuelve a encajar en la tarjeta
    };

    // Si hubo arrastre, anula el clic para no abrir el tour al soltar.
    const onClickCapture = (e: MouseEvent) => {
      if (moved) {
        e.preventDefault();
        e.stopPropagation();
        moved = false;
      }
    };

    // Evita el "drag fantasma" de imágenes/enlaces dentro del carrusel.
    const onDragStart = (e: Event) => e.preventDefault();

    el.style.cursor = 'grab';
    el.addEventListener('pointerdown', onPointerDown);
    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', endDrag);
    window.addEventListener('pointercancel', endDrag);
    el.addEventListener('click', onClickCapture, true);
    el.addEventListener('dragstart', onDragStart);

    return () => {
      el.removeEventListener('pointerdown', onPointerDown);
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', endDrag);
      window.removeEventListener('pointercancel', endDrag);
      el.removeEventListener('click', onClickCapture, true);
      el.removeEventListener('dragstart', onDragStart);
      el.style.removeProperty('cursor');
      el.style.removeProperty('user-select');
    };
  }, [ref, enabled]);
}
